import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createBook, updateBook, getBookById } from '../utils/api';
import Header from '../components/Header';
import { getAllDocumentTypes, deleteDocument, getAllSubjects, getAllClasses } from '../utils/api';

interface Document {
  id: string;
  type: 'sample_questions' | 'sample_pages' | 'study_guide' | 'solution' | 'other';
  title: string;
  file?: File;
  fileUrl?: string;
  _id?: string;
}

interface BookData {
  title: string;
  authors: string;
  publication: string;
  cost: number;
  quantityInStock: number;
  description?: string;
  year?: number;
  pages?: number;
  isbn?: string;
  sku?: string;
  isFeatured?: boolean;
  mainImage?: File | string;
  additionalImages?: (File | string)[];
  subjects?: string[];
  classes?: string[];
  documents?: Document[];
}

interface TabType {
  type: 'basic' | 'images' | 'documents';
  label: string;
}

const BookForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [activeTab, setActiveTab] = useState<'basic' | 'images' | 'documents'>('basic');
  const [loading, setLoading] = useState(id ? true : false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [draggedDocId, setDraggedDocId] = useState<string | null>(null);
  
  const mainImageInputRef = React.useRef<HTMLInputElement>(null);
  const additionalImagesInputRef = React.useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<BookData>({
    title: '',
    authors: '',
    publication: '',
    cost: 0,
    quantityInStock: 0,
    description: '',
    year: new Date().getFullYear(),
    pages: 0,
    isbn: '',
    sku: '',
    isFeatured: false,
    mainImage: undefined,
    additionalImages: [],
    subjects: [],
    classes: [],
    documents: [],
  });

  const [mainImagePreview, setMainImagePreview] = useState<string>('');
  const [additionalImagesPreview, setAdditionalImagesPreview] = useState<string[]>([]);

  const fetchBook = React.useCallback(async () => {
    try {
      if (id) {
        const book = await getBookById(id);
        const authorsArray = Array.isArray(book.authors) ? book.authors : [];
        setFormData({
          title: book.title,
          authors: authorsArray.join(', '),
          publication: book.publication,
          cost: book.cost,
          quantityInStock: book.quantityInStock,
          description: book.description || '',
          year: book.year || new Date().getFullYear(),
          pages: book.pages || 0,
          isbn: book.isbn || '',
          sku: book.sku || '',
          isFeatured: Boolean(book.isFeatured),
          mainImage: book.mainImage || undefined,
          additionalImages: book.additionalImages || [],
          subjects: (book.subjects || []).map((subject: any) => subject._id || subject),
          classes: (book.classes || []).map((classLevel: any) => classLevel._id || classLevel),
          documents: (book.documents || []).map((d: any, i: number) => ({
            id: d._id || d.id || `doc-${i}`,
            _id: d._id,
            type: d.documentType || d.type,
            title: d.title,
            fileUrl: d.fileUrl || (d.fileName ? `/uploads/documents/${d.fileName}` : ''),
            fileName: d.fileName,
            filePath: d.filePath,
          })),
        });
        if (book.mainImage) {
          setMainImagePreview(book.mainImage);
        }
        if (book.additionalImages?.length > 0) {
          setAdditionalImagesPreview(book.additionalImages);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch book');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchBook();
    }
  }, [fetchBook, id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: ['cost', 'quantityInStock', 'year', 'pages'].includes(name) ? parseFloat(value) : value,
    }));
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({ ...prev, mainImage: file }));
      const reader = new FileReader();
      reader.onload = (event) => {
        setMainImagePreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const newImages = [...(formData.additionalImages || []), ...files];
      setFormData((prev) => ({ ...prev, additionalImages: newImages as any }));
      
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          setAdditionalImagesPreview((prev) => [...prev, event.target?.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeAdditionalImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      additionalImages: (prev.additionalImages || []).filter((_, i) => i !== index),
    }));
    setAdditionalImagesPreview((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDocumentAdd = () => {
    const newDoc: Document = {
      id: Date.now().toString(),
      type: 'sample_questions',
      title: '',
    };
    setFormData((prev) => ({
      ...prev,
      documents: [...(prev.documents || []), newDoc],
    }));
  };

  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const [types, subjectList, classList] = await Promise.all([
          getAllDocumentTypes(),
          getAllSubjects(),
          getAllClasses(),
        ]);
        setDocumentTypes(types || []);
        setSubjects(subjectList || []);
        setClasses(classList || []);
      } catch (e) {
        // ignore
      }
    };
    loadTypes();
  }, []);

  const handleDocumentChange = (docId: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      documents: (prev.documents || []).map((doc) =>
        doc.id === docId ? { ...doc, [field]: value } : doc
      ),
    }));
  };

  const handleDocumentFileChange = (docId: string, file: File) => {
    handleDocumentChange(docId, 'file', file);
  };

  const removeDocument = (docId: string) => {
    const doc = formData.documents?.find((d) => d.id === docId || d._id === docId);
    if (!doc) return;
    // If document exists on server, call delete API
    if (doc._id) {
      if (!window.confirm('Are you sure you want to delete this document?')) return;
      deleteDocument(doc._id)
        .then(() => {
          setFormData((prev) => ({
            ...prev,
            documents: (prev.documents || []).filter((d) => d._id !== doc._id),
          }));
        })
        .catch((err) => {
          alert(err.response?.data?.message || 'Failed to delete document');
        });
      return;
    }

    // Otherwise just remove locally
    setFormData((prev) => ({
      ...prev,
      documents: (prev.documents || []).filter((doc) => doc.id !== docId),
    }));
  };

  const removeExistingFile = (docId: string) => {
    setFormData((prev) => ({
      ...prev,
      documents: (prev.documents || []).map((doc) =>
        doc.id === docId ? { ...doc, fileUrl: undefined } : doc
      ),
    }));
  };

  const handleDragStart = (e: React.DragEvent, docId: string) => {
    setDraggedDocId(docId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropDocument = (e: React.DragEvent, targetDocId: string) => {
    e.preventDefault();
    if (!draggedDocId || draggedDocId === targetDocId) {
      setDraggedDocId(null);
      return;
    }

    const docs = [...(formData.documents || [])];
    const draggedIdx = docs.findIndex((d) => d.id === draggedDocId);
    const targetIdx = docs.findIndex((d) => d.id === targetDocId);

    if (draggedIdx !== -1 && targetIdx !== -1) {
      [docs[draggedIdx], docs[targetIdx]] = [docs[targetIdx], docs[draggedIdx]];
      setFormData((prev) => ({ ...prev, documents: docs }));
    }
    setDraggedDocId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append('title', formData.title);
      formDataObj.append('authors', JSON.stringify(formData.authors.split(',').map((a) => a.trim())));
      formDataObj.append('publication', formData.publication);
      formDataObj.append('cost', formData.cost.toString());
      formDataObj.append('quantityInStock', formData.quantityInStock.toString());
      formDataObj.append('description', formData.description || '');
      formDataObj.append('year', (formData.year || new Date().getFullYear()).toString());
      formDataObj.append('pages', (formData.pages || 0).toString());
      formDataObj.append('isbn', formData.isbn || '');
      formDataObj.append('sku', formData.sku || '');
      formDataObj.append('isFeatured', String(Boolean(formData.isFeatured)));
      formDataObj.append('subjects', JSON.stringify(formData.subjects || []));
      formDataObj.append('classes', JSON.stringify(formData.classes || []));

      if (formData.mainImage instanceof File) {
        formDataObj.append('mainImage', formData.mainImage);
      }

      (formData.additionalImages || []).forEach((img, idx) => {
        if (img instanceof File) {
          formDataObj.append(`additionalImages`, img);
        }
      });

      (formData.documents || []).forEach((doc, idx) => {
        formDataObj.append(`documents[${idx}][type]`, doc.type);
        formDataObj.append(`documents[${idx}][title]`, doc.title);
        if (doc.file) {
          formDataObj.append(`documents[${idx}][file]`, doc.file);
        } else if (doc.fileUrl) {
          // preserve existing file URL when no new file is uploaded
          formDataObj.append(`documents[${idx}][fileUrl]`, doc.fileUrl);
        }
      });

      if (id) {
        await updateBook(id, formDataObj);
        alert('Book updated successfully');
      } else {
        await createBook(formDataObj);
        alert('Book created successfully');
      }
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save book');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header title={id ? 'Edit Book' : 'Create New Book'} />
        <div className="book-form-container">
          <p>Loading...</p>
        </div>
      </>
    );
  }

  const tabs: TabType[] = [
    { type: 'basic', label: 'Basic Information' },
    { type: 'images', label: 'Images' },
    ...(id ? [{ type: 'documents' as const, label: 'Documents' }] : []),
  ];

  return (
    <>
      <Header title={id ? 'Edit Book' : 'Create New Book'} />
      <div className="book-form-container">
        <div className="book-form">
          <div className="tabs">
            {tabs.map((tab) => (
              <button
                key={tab.type}
                type="button"
                className={`tab-button ${activeTab === tab.type ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.type)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="Enter book title"
                  />
                </div>

                <div className="form-group">
                  <label>Authors (comma separated) *</label>
                  <input
                    type="text"
                    name="authors"
                    value={formData.authors}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Author One, Author Two"
                  />
                </div>

                <div className="form-group">
                  <label>Publication *</label>
                  <input
                    type="text"
                    name="publication"
                    value={formData.publication}
                    onChange={handleChange}
                    required
                    placeholder="Enter publication name"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Year</label>
                    <input
                      type="number"
                      name="year"
                      value={formData.year || ''}
                      onChange={handleChange}
                      min="1900"
                      max={new Date().getFullYear()}
                      placeholder="YYYY"
                    />
                  </div>

                  <div className="form-group">
                    <label>Pages</label>
                    <input
                      type="number"
                      name="pages"
                      value={formData.pages || ''}
                      onChange={handleChange}
                      min="0"
                      placeholder="Number of pages"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>ISBN Code</label>
                    <input
                      type="text"
                      name="isbn"
                      value={formData.isbn || ''}
                      onChange={handleChange}
                      placeholder="Enter ISBN code"
                    />
                  </div>

                  <div className="form-group">
                    <label>SKU</label>
                    <input
                      type="text"
                      name="sku"
                      value={formData.sku || ''}
                      onChange={handleChange}
                      placeholder="Enter SKU"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(formData.isFeatured)}
                      onChange={(e) => setFormData((prev) => ({ ...prev, isFeatured: e.target.checked }))}
                    />
                    {' '}Show this book in the mobile home green banner
                  </label>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Cost (₹) *</label>
                    <input
                      type="number"
                      name="cost"
                      value={formData.cost}
                      onChange={handleChange}
                      required
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label>Quantity in Stock *</label>
                    <input
                      type="number"
                      name="quantityInStock"
                      value={formData.quantityInStock}
                      onChange={handleChange}
                      required
                      min="0"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Subjects</label>
                    <select
                      multiple
                      value={formData.subjects || []}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          subjects: Array.from(e.target.selectedOptions).map((option) => option.value),
                        }))
                      }
                    >
                      {subjects.map((subject) => (
                        <option key={subject._id} value={subject._id}>
                          {subject.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Classes</label>
                    <select
                      multiple
                      value={formData.classes || []}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          classes: Array.from(e.target.selectedOptions).map((option) => option.value),
                        }))
                      }
                    >
                      {classes.map((classLevel) => (
                        <option key={classLevel._id} value={classLevel._id}>
                          {classLevel.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleChange}
                    placeholder="Enter book description"
                    rows={4}
                  />
                </div>
              </>
            )}

            {/* Images Tab */}
            {activeTab === 'images' && (
              <>
                <div className="form-group">
                  <label>Main Image</label>
                  <div
                    className="image-upload-container"
                    onClick={() => mainImageInputRef.current?.click()}
                  >
                    <input
                      ref={mainImageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleMainImageChange}
                      style={{ display: 'none' }}
                    />
                    <div className="upload-prompt">
                      <div className="upload-icon">📷</div>
                      <div className="upload-text">
                        <strong>Click to upload</strong> or drag and drop
                      </div>
                      <div className="upload-hint">PNG, JPG, GIF up to 10MB</div>
                    </div>
                    {mainImagePreview && (
                                <div className="image-preview">
                                  <a href={mainImagePreview} target="_blank" rel="noreferrer">
                                    <img src={mainImagePreview} alt="Main book cover" />
                                  </a>
                                </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Additional Images</label>
                  <div
                    className="image-upload-container"
                    onClick={() => additionalImagesInputRef.current?.click()}
                  >
                    <input
                      ref={additionalImagesInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleAdditionalImagesChange}
                      style={{ display: 'none' }}
                    />
                    <div className="upload-prompt">
                      <div className="upload-icon">🖼️</div>
                      <div className="upload-text">
                        <strong>Click to upload</strong> or drag and drop
                      </div>
                      <div className="upload-hint">Add multiple images (PNG, JPG, GIF)</div>
                    </div>
                    <div className="images-grid">
                      {additionalImagesPreview.map((preview, idx) => (
                        <div key={idx} className="image-item">
                          <a href={preview} target="_blank" rel="noreferrer">
                            <img src={preview} alt={`Book ${idx + 1}`} />
                          </a>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeAdditionalImage(idx);
                            }}
                            className="btn-remove-image"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Documents Tab (Edit Only) */}
            {activeTab === 'documents' && id && (
              <>
                <div className="documents-header">
                  <h3>Book Documents</h3>
                  <button
                    type="button"
                    onClick={handleDocumentAdd}
                    className="btn-add-document"
                  >
                    + Add Document
                  </button>
                </div>

                <div className="documents-list">
                  {(formData.documents || []).map((doc, idx) => (
                    <div
                      key={doc.id}
                      className={`document-item ${draggedDocId === doc.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, doc.id)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDropDocument(e, doc.id)}
                    >
                      <div className="document-drag-handle">≡</div>
                      <div className="document-form">
                        <div className="form-group">
                          <label>Document Type</label>
                          <select
                            value={doc.type}
                            onChange={(e) =>
                              handleDocumentChange(doc.id, 'type', e.target.value)
                            }
                          >
                            {documentTypes.map((t) => (
                              <option key={t._id} value={t._id}>{t.name}</option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>Document Title</label>
                          <input
                            type="text"
                            value={doc.title}
                            onChange={(e) =>
                              handleDocumentChange(doc.id, 'title', e.target.value)
                            }
                            placeholder="Enter document title"
                          />
                        </div>

                        <div className="form-group">
                          <label>Upload File</label>
                          <input
                            type="file"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleDocumentFileChange(doc.id, file);
                              }
                            }}
                          />
                          {doc.fileUrl && !doc.file && (
                            <div className="existing-file">
                              <a href={doc.fileUrl} target="_blank" rel="noreferrer">
                                View uploaded file
                              </a>
                              <button
                                type="button"
                                onClick={() => removeExistingFile(doc.id)}
                                className="btn-remove-file"
                              >
                                Remove file
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeDocument(doc.id)}
                        className="btn-remove-document"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
                {/* Existing documents rendered as document-items above; removed duplicate table */}
              </>
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button type="submit" disabled={submitting} className="btn-primary">
                {submitting ? 'Saving...' : id ? 'Update Book' : 'Create Book'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default BookForm;
